import { useEffect, useRef, useState } from 'react';
import { confirmSignIn, signIn, signOut } from 'aws-amplify/auth';
import { Controller, useForm } from 'react-hook-form';
import { Keyboard, LoaderCircle, MapPin, Package2 } from 'lucide-react';
import { CURRENT_ENV, SCREEN_TYPES } from 'utils/constants';
import { checkUser, getStations } from 'services/api/auth';
import { useAppStore, useAuthStore } from 'store';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { useKeyboardInputToggle } from 'hooks/useKeyboardInputToggle';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { getSessionUser } from '../utils';
import { ChangePassword } from './ChangePassword';
import { ForgotPassword } from './ForgotPassword';
import { LanguageToggle } from './LanguageToggle';

type LoginFormValues = {
  email: string;
  password: string;
  stationId: string;
};

type Station = {
  station_id: string;
  station_name: string;
};

export default function Login() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<
    'identify' | 'login' | 'changePassword' | 'selectStation' | 'forgotPassword'
  >('identify');
  const [stations, setStations] = useState<Station[]>([]);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const { keyboardVisible, toggleKeyboardInput } = useKeyboardInputToggle(emailInputRef);
  const { stationId, setStationId, setAuthenticatedUser, setAuthMode } = useAuthStore();
  const { setCurrentScreen } = useAppStore();

  const { register, handleSubmit, watch, setValue, control } = useForm<LoginFormValues>({
    defaultValues: {
      email: '',
      password: '',
      stationId: stationId || '',
    },
  });

  const emailValue = watch('email');
  const passwordValue = watch('password');
  const selectedStationId = watch('stationId');
  const { ref: emailFieldRef, ...emailFieldProps } = register('email');
  const { ref: passwordFieldRef, ...passwordFieldProps } = register('password');

  useEffect(() => {
    if (loading) {
      return;
    }

    const inputRef =
      step === 'identify' ? emailInputRef : step === 'login' ? passwordInputRef : null;
    if (!inputRef) {
      return;
    }

    const focusTimer = window.setTimeout(() => inputRef.current?.focus(), 0);

    return () => window.clearTimeout(focusTimer);
  }, [step, loading]);

  useEffect(() => {
    if (step !== 'identify') {
      return;
    }

    const interval = window.setInterval(() => {
      const el = emailInputRef.current;
      if (el && !el.disabled && document.activeElement !== el) {
        el.focus();
      }
    }, 200);

    return () => window.clearInterval(interval);
  }, [step]);

  const trySignIn = async (input: Parameters<typeof signIn>[0]) => {
    try {
      return await signIn(input);
    } catch (error) {
      const err = error as { message?: string; name?: string };

      if (
        err.name === 'UserAlreadyAuthenticatedException' ||
        err.message?.toLowerCase().includes('already')
      ) {
        await signOut();
        return signIn(input);
      }

      throw error;
    }
  };

  const completeLogin = (selectedStation: string) => {
    setStationId(selectedStation);
    setCurrentScreen(SCREEN_TYPES.APP_SELECT);
    toast.success(t('toast.loginSuccess'));
  };

  const proceedToStationSelection = async (): Promise<void> => {
    const stationsResponse = await getStations();
    const nextStations: Station[] = Array.isArray(stationsResponse?.stations)
      ? stationsResponse.stations
      : [];

    if (!nextStations.length) {
      throw new Error(t('login.noStations'));
    }

    setStations(nextStations);

    if (nextStations.length === 1) {
      const onlyStationId = nextStations[0].station_id;
      completeLogin(onlyStationId);
      return;
    }

    if (stationId && nextStations.some((station) => station.station_id === stationId)) {
      setValue('stationId', stationId);
      completeLogin(stationId);
      return;
    }

    setStep('selectStation');
  };

  const handleIdentifySubmit = async ({ email }: LoginFormValues): Promise<void> => {
    const identifier = email.trim();
    if (!identifier) {
      toast.warning(t('toast.pleaseEnterIdentifier'));
      return;
    }

    setLoading(true);

    try {
      const result = await checkUser(identifier);

      if (!result.exists) {
        toast.error(t('toast.noAccountFound'));
        return;
      }

      if (!result.enabled) {
        toast.error(t('toast.accountDisabled'));
        return;
      }

      const username = result.username ?? result.userId ?? identifier;

      setAuthMode(result.passwordless ? 'passwordless' : 'password');

      if (result.passwordless) {
        const signInResult = await trySignIn({
          username,
          options: { authFlowType: 'CUSTOM_WITHOUT_SRP' },
        });

        if (signInResult.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE') {
          await confirmSignIn({ challengeResponse: 'PASSWORDLESS_CONFIRM' });
        }

        const sessionUser = await getSessionUser();
        setAuthenticatedUser(sessionUser);

        await proceedToStationSelection();
        return;
      }

      setValue('email', username);
      setStep('login');
    } catch (error) {
      const err = error as { message?: string };
      toast.error(err.message || t('toast.signInFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async ({ email, password }: LoginFormValues): Promise<void> => {
    if (!email || !password) {
      toast.warning(t('toast.pleaseEnterCredentials'));
      return;
    }

    setLoading(true);

    try {
      const signInResult = await trySignIn({ username: email, password });

      if (signInResult.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        setStep('changePassword');
        return;
      }

      const sessionUser = await getSessionUser();
      setAuthenticatedUser(sessionUser);

      await proceedToStationSelection();
    } catch (error) {
      const err = error as { message?: string };
      toast.error(err.message || t('toast.loginFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSelectStation = async ({ stationId }: LoginFormValues): Promise<void> => {
    if (!stationId) {
      toast.warning(t('toast.pleaseSelectStation'));
      return;
    }

    setLoading(true);

    try {
      completeLogin(stationId);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = async () => {
    setLoading(true);

    try {
      try {
        await signOut();
      } catch {
        // ignore signOut failure here
      }

      setStep('identify');
      setStations([]);
      setAuthenticatedUser(null);
      setAuthMode(null);
      setValue('stationId', '');
      setValue('email', '');
      setValue('password', '');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full px-4 py-6">
      <div className="mx-auto w-full max-w-[390px]">
        <Card className="overflow-hidden border-slate-200/80 bg-white/95 backdrop-blur">
          <CardHeader className="space-y-5 bg-gradient-to-b from-slate-50 via-white to-white pb-4">
            <div className="flex items-start justify-between">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                <Package2 className="h-7 w-7" />
              </div>
              <LanguageToggle />
            </div>

            <CardTitle className="text-left text-2xl font-semibold text-slate-900">
              {t('login.title')}
            </CardTitle>

            <p className="text-left text-sm leading-6 text-slate-500">{t('login.subtitle')}</p>

            <span className="inline-block rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium ">
              {t('login.currentEnvironment')} <b className="text-primary">{CURRENT_ENV}</b>
            </span>
          </CardHeader>

          <CardContent className="space-y-6">
            {step === 'changePassword' ? (
              <ChangePassword
                onSuccess={() => {
                  setStep('login');
                  setValue('password', '');
                }}
                onBack={handleBack}
              />
            ) : step === 'forgotPassword' ? (
              <ForgotPassword onBack={() => setStep('login')} />
            ) : step === 'identify' ? (
              <form className="space-y-5" onSubmit={handleSubmit(handleIdentifySubmit)}>
                <div className="space-y-2">
                  <Label htmlFor="email">{t('login.identifier')}</Label>
                  <div className="flex items-center gap-2.5">
                    <Input
                      id="email"
                      inputMode={keyboardVisible ? 'text' : 'none'}
                      autoComplete="off"
                      spellCheck={false}
                      placeholder={t('login.identifierPlaceholder')}
                      disabled={loading}
                      {...emailFieldProps}
                      ref={(element) => {
                        emailFieldRef(element);
                        emailInputRef.current = element;
                      }}
                      onBlur={(e) => {
                        emailFieldProps.onBlur(e);
                        if (!loading) {
                          requestAnimationFrame(() => emailInputRef.current?.focus());
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          void handleSubmit(handleIdentifySubmit)();
                        }
                      }}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-12 w-12 shrink-0 rounded-xl border-slate-300"
                      onClick={toggleKeyboardInput}
                      aria-pressed={keyboardVisible}
                    >
                      <Keyboard className="h-5 w-5" />
                    </Button>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="h-12 w-full rounded-2xl text-base font-semibold"
                  disabled={!emailValue || loading}
                >
                  {loading ? <LoaderCircle className="h-4 w-4 animate-spin" /> : null}
                  {loading ? t('login.checking') : t('login.continueButton')}
                </Button>
              </form>
            ) : step === 'login' ? (
              <form className="space-y-5" onSubmit={handleSubmit(handleLogin)}>
                <div className="space-y-1 text-sm text-slate-500">
                  {t('login.signingInAs')}{' '}
                  <span className="font-medium text-slate-700">{emailValue}</span>
                  <button
                    type="button"
                    className="ml-2 text-primary hover:underline"
                    onClick={() => setStep('identify')}
                  >
                    {t('login.notYou')}
                  </button>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">{t('login.password')}</Label>
                  <Input
                    id="password"
                    isPassword
                    autoComplete="current-password"
                    placeholder={t('login.passwordPlaceholder')}
                    disabled={loading}
                    {...passwordFieldProps}
                    ref={(element) => {
                      passwordFieldRef(element);
                      passwordInputRef.current = element;
                    }}
                  />
                </div>

                <div className="text-right">
                  <button
                    type="button"
                    className="text-sm text-primary hover:underline"
                    onClick={() => setStep('forgotPassword')}
                  >
                    {t('login.forgotPassword')}
                  </button>
                </div>

                <Button
                  type="submit"
                  className="h-12 w-full rounded-2xl text-base font-semibold"
                  disabled={!passwordValue || loading}
                >
                  {loading ? <LoaderCircle className="h-4 w-4 animate-spin" /> : null}
                  {loading ? t('login.loggingIn') : t('login.loginButton')}
                </Button>
              </form>
            ) : (
              <form className="space-y-5" onSubmit={handleSubmit(handleSelectStation)}>
                <div className="space-y-2">
                  <Label htmlFor="stationId">{t('login.selectStation')}</Label>

                  <Controller
                    name="stationId"
                    control={control}
                    render={({ field }) => (
                      <div className="relative">
                        <div className="pointer-events-none absolute left-4 top-1/2 z-10 -translate-y-1/2 text-primary">
                          <MapPin className="h-4 w-4" />
                        </div>

                        <Select
                          disabled={loading}
                          value={field.value}
                          onValueChange={(value) => {
                            field.onChange(value);
                            setStationId(value);
                          }}
                        >
                          <SelectTrigger id="stationId" className="pl-11">
                            <SelectValue placeholder={t('login.selectStationPlaceholder')} />
                          </SelectTrigger>

                          <SelectContent>
                            {stations.map((station) => (
                              <SelectItem key={station.station_id} value={station.station_id}>
                                {station.station_name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  />
                </div>

                <div className="space-y-3">
                  <Button
                    type="submit"
                    className="h-12 w-full rounded-2xl text-base font-semibold"
                    disabled={!selectedStationId || loading}
                  >
                    {loading ? <LoaderCircle className="h-4 w-4 animate-spin" /> : null}
                    {loading ? t('login.continuing') : t('login.continueButton')}
                  </Button>

                  <Button
                    type="button"
                    variant="outline"
                    className="h-12 w-full rounded-2xl text-base"
                    disabled={loading}
                    onClick={handleBack}
                  >
                    {t('login.back')}
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
